import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/core/composables/useFetch'
import type {
  Message,
  CreateMessagePayload,
  RecipientPreview,
  MessageReceiverAttribute,
  EntityLookupItem,
} from '../types'

export const useMessagingStore = defineStore('messaging/messagingStore', () => {
  const route = useRoute()
  const baseUrl = computed(() => `/v4/${route.params.slug}/messaging`)

  const messages = ref<Message[]>([])
  const currentMessage = ref<Message | null>(null)

  const fetchMessages = async () => {
    const data = await useFetch(`${baseUrl.value}/messages`).get<Message[]>()
    messages.value = data
  }

  const fetchMessage = async (id: number | string) => {
    const data = await useFetch(`${baseUrl.value}/messages/${id}`).get<Message>()
    currentMessage.value = data
  }

  const refreshMessageLogs = async (id: number | string) => {
    const data = await useFetch(`${baseUrl.value}/messages/${id}`).get<Message>()
    if (currentMessage.value && currentMessage.value.id === data.id) {
      currentMessage.value.message_logs = data.message_logs
      currentMessage.value.percentage_opened = data.percentage_opened
    }
  }

  const createMessage = async (payload: CreateMessagePayload) => {
    const hasFiles = payload.files && payload.files.length > 0

    if (hasFiles) {
      const formData = new FormData()
      formData.append('subject', payload.subject)
      formData.append('body', payload.body)
      formData.append('copy_me', String(payload.copy_me))

      payload.message_receivers_attributes.forEach((receiver, i) => {
        formData.append(`message_receivers_attributes[${i}][receiver_group]`, receiver.receiver_group)
        if (receiver.extra_info) {
          Object.entries(receiver.extra_info).forEach(([key, value]) => {
            if (value === undefined || value === '') return
            if (Array.isArray(value)) {
              value.forEach((v) => {
                formData.append(`message_receivers_attributes[${i}][extra_info][${key}][]`, String(v))
              })
            } else {
              formData.append(`message_receivers_attributes[${i}][extra_info][${key}]`, String(value))
            }
          })
        }
      })

      payload.files!.forEach((file) => {
        formData.append('files[]', file)
      })

      return await useFetch(`${baseUrl.value}/messages`).post<Message>(formData)
    }

    return await useFetch(`${baseUrl.value}/messages`).post<Message>({
      subject: payload.subject,
      body: payload.body,
      copy_me: payload.copy_me,
      message_receivers_attributes: payload.message_receivers_attributes,
    })
  }

  const destroyMessage = async (id: number | string) => {
    await useFetch(`${baseUrl.value}/messages/${id}/destroy`).post()
    messages.value = messages.value.filter((m) => m.id !== id)
    if (currentMessage.value?.id === id) {
      currentMessage.value = null
    }
  }

  const fetchLookups = async (receiverGroup: string) => {
    const data = await useFetch(`${baseUrl.value}/lookups?receiver_group=${receiverGroup}`).get<EntityLookupItem[]>()
    return data
  }

  const previewRecipients = async (receivers: MessageReceiverAttribute[]) => {
    const data = await useFetch(`${baseUrl.value}/messages/recipients`).post<RecipientPreview>({
      message_receivers_attributes: receivers,
    })
    return data
  }

  const resetStore = () => {
    messages.value = []
    currentMessage.value = null
  }

  return {
    messages,
    currentMessage,
    fetchMessages,
    fetchMessage,
    refreshMessageLogs,
    createMessage,
    destroyMessage,
    fetchLookups,
    previewRecipients,
    resetStore,
  }
})
